import { useEffect, useState } from 'react';
import { BarChart3, Download, KeyRound, RefreshCw, Save, Send, Trash2, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  marketingSettingsApi,
  type ConversionActionDto,
  type ConversionEventDto,
  type MarketingConversionDto,
  type MarketingSettingsDto,
  type MarketingSettingsUpdateDto,
} from '../../../lib/api';

const defaults: MarketingSettingsDto = {
  enabled: false,
  gtmContainerId: '',
  ga4MeasurementId: '',
  googleAdsConversionId: '',
  conversionEvents: {},
  conversionActions: [],
  metaPixelId: '',
  metaCapiTestEventCode: '',
  serverEventsEnabled: false,
  attributionCookieDays: 90,
  captureUtm: true,
  captureClickIds: true,
  captureReferrer: true,
  metaCapiAccessTokenIsSet: false,
};

/**
 * Baut das Speicher-Payload. `metaCapiAccessTokenIsSet` und `conversionActions`
 * sind rein lesend und dürfen nicht zurückgeschickt werden; beim Token bedeutet
 * '' "unverändert lassen" und `null` "löschen".
 */
function buildUpdatePayload(
  settings: MarketingSettingsDto,
  events: Record<string, ConversionEventDto>,
  token: string | null,
): MarketingSettingsUpdateDto {
  return {
    enabled: settings.enabled,
    gtmContainerId: (settings.gtmContainerId || '').trim().toUpperCase(),
    ga4MeasurementId: (settings.ga4MeasurementId || '').trim().toUpperCase(),
    googleAdsConversionId: (settings.googleAdsConversionId || '').trim().toUpperCase(),
    conversionEvents: events,
    metaPixelId: (settings.metaPixelId || '').trim(),
    metaCapiTestEventCode: (settings.metaCapiTestEventCode || '').trim(),
    metaCapiAccessToken: token,
    serverEventsEnabled: settings.serverEventsEnabled,
    attributionCookieDays: settings.attributionCookieDays,
    captureUtm: settings.captureUtm,
    captureClickIds: settings.captureClickIds,
    captureReferrer: settings.captureReferrer,
  };
}

/** Fehlende Aktionen mit ihren Voreinstellungen auffüllen, damit kein Feld leer startet. */
function withActionDefaults(
  events: Record<string, ConversionEventDto> | undefined,
  actions: ConversionActionDto[],
): Record<string, ConversionEventDto> {
  const out: Record<string, ConversionEventDto> = {};
  for (const action of actions) {
    const entry = events?.[action.key];
    out[action.key] = {
      enabled: entry?.enabled !== false,
      googleEvent: entry?.googleEvent || action.defaultGoogleEvent,
      googleAdsLabel: entry?.googleAdsLabel || '',
      metaEvent: entry?.metaEvent || action.defaultMetaEvent,
    };
  }
  return out;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString('de-DE');
}

export default function MarketingSettingsPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState<MarketingSettingsDto>(defaults);
  const [actions, setActions] = useState<ConversionActionDto[]>([]);
  const [events, setEvents] = useState<Record<string, ConversionEventDto>>({});
  const [conversions, setConversions] = useState<MarketingConversionDto[]>([]);
  const [pendingExport, setPendingExport] = useState(0);
  const [exporting, setExporting] = useState(false);
  // Welche Aktion der Test-Button an Meta schickt.
  const [testAction, setTestAction] = useState('trial_request');
  // Klartext-Token nur solange es im Formular steht; der Server liefert es nie zurück.
  const [capiToken, setCapiToken] = useState('');

  const applyLoaded = (data: MarketingSettingsDto) => {
    const merged = { ...defaults, ...data };
    const catalog = merged.conversionActions ?? [];
    setSettings(merged);
    setActions(catalog);
    setEvents(withActionDefaults(merged.conversionEvents, catalog));
    setCapiToken('');
  };

  const load = async () => {
    setLoading(true);
    try {
      applyLoaded(await marketingSettingsApi.get());
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Marketing-Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadConversions = async () => {
    try {
      const data = await marketingSettingsApi.listConversions();
      setConversions(data.items);
      setPendingExport(data.pendingExportCount);
    } catch {
      // Rein informativ — eine leere Liste ist kein Grund, die Seite zu stören.
    }
  };

  useEffect(() => {
    load();
    loadConversions();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Leer = Token unverändert lassen (die UI kennt den Klartext nicht).
      const payload = buildUpdatePayload(settings, events, capiToken.trim());
      applyLoaded(await marketingSettingsApi.update(payload));
      toast.success('Marketing-Einstellungen wurden gespeichert');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const clearToken = async () => {
    setSaving(true);
    try {
      applyLoaded(await marketingSettingsApi.update(buildUpdatePayload(settings, events, null)));
      toast.success('CAPI-Token wurde entfernt');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Token konnte nicht entfernt werden');
    } finally {
      setSaving(false);
    }
  };

  const setEventField = (key: string, field: keyof ConversionEventDto, value: string | boolean) => {
    setEvents((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const downloadExport = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await marketingSettingsApi.downloadConversionExport(true);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV heruntergeladen – in Google Ads unter „Conversions importieren“ hochladen');
      await loadConversions();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const sendTestEvent = async () => {
    setTesting(true);
    try {
      // Getestet wird die gewählte Aktion mit dem Namen, der auch im
      // Echtbetrieb rausgeht — ungespeicherte Änderungen gelten also noch nicht.
      const result = await marketingSettingsApi.sendTestEvent({ action: testAction });
      if (result.sent) {
        toast.success(`Test-Event „${result.eventName}“ an Meta gesendet – im Events Manager prüfen`);
      } else {
        toast.error(`Meta hat das Event abgelehnt: ${result.reason || result.response || 'unbekannter Fehler'}`);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Test-Event fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  const busy = loading || saving;
  const tokenSet = settings.metaCapiAccessTokenIsSet === true;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Marketing & Tracking</h1>
        <p className="text-muted-foreground">
          Tracking-IDs für die Website (GTM, GA4, Google Ads, Meta) und Attribution (UTM, Click-IDs, Referrer).
        </p>
        <div className="mt-3">
          <Button asChild variant="outline" size="sm">
            <Link to="/system/marketing/gutscheincodes">Zu Gutscheincodes</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Tracking-Konfiguration
          </CardTitle>
          <CardDescription>
            Diese Werte werden für die Website genutzt und bei Trial-Requests in der Datenbank gespeichert.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Marketing-Tracking aktiv</div>
              <div className="text-xs text-muted-foreground">
                Hauptschalter: ohne ihn lädt die Website keinerlei Tracking-Tags.
              </div>
            </div>
            <Switch
              checked={!!settings.enabled}
              onCheckedChange={(v) => setSettings((prev) => ({ ...prev, enabled: v }))}
              disabled={busy}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>GTM Container-ID</Label>
              <Input
                value={settings.gtmContainerId || ''}
                onChange={(e) => setSettings((prev) => ({ ...prev, gtmContainerId: e.target.value }))}
                placeholder="GTM-XXXXXXX"
                disabled={busy}
              />
              <p className="text-xs text-muted-foreground">
                Beispiel: <code>GTM-ABC1234</code>
              </p>
            </div>
            <div className="space-y-2">
              <Label>GA4 Measurement-ID</Label>
              <Input
                value={settings.ga4MeasurementId || ''}
                onChange={(e) => setSettings((prev) => ({ ...prev, ga4MeasurementId: e.target.value }))}
                placeholder="G-XXXXXXXXXX"
                disabled={busy}
              />
              <p className="text-xs text-muted-foreground">
                Nur ausfüllen, wenn GA4 <strong>nicht</strong> im GTM-Container steckt – sonst werden Seitenaufrufe
                doppelt gezählt.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Google Ads Conversion-ID</Label>
              <Input
                value={settings.googleAdsConversionId || ''}
                onChange={(e) => setSettings((prev) => ({ ...prev, googleAdsConversionId: e.target.value }))}
                placeholder="AW-123456789"
                disabled={busy}
              />
              <p className="text-xs text-muted-foreground">
                Google Ads teilt sich den gtag-Loader mit GA4 – eine der beiden IDs genügt zum Laden.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Attribution Cookie (Tage)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={settings.attributionCookieDays ?? 90}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, attributionCookieDays: Number(e.target.value || 90) }))
                }
                disabled={busy}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="text-sm">UTM Parameter erfassen</div>
              <Switch
                checked={!!settings.captureUtm}
                onCheckedChange={(v) => setSettings((prev) => ({ ...prev, captureUtm: v }))}
                disabled={busy}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="text-sm">Click IDs erfassen</div>
              <Switch
                checked={!!settings.captureClickIds}
                onCheckedChange={(v) => setSettings((prev) => ({ ...prev, captureClickIds: v }))}
                disabled={busy}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="text-sm">Referrer erfassen</div>
              <Switch
                checked={!!settings.captureReferrer}
                onCheckedChange={(v) => setSettings((prev) => ({ ...prev, captureReferrer: v }))}
                disabled={busy}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Conversion-Events</CardTitle>
          <CardDescription>
            Pro Aktion im Trichter lässt sich festlegen, wie sie bei Google und bei Meta heißen soll. Die Namen
            müssen exakt denen entsprechen, die in Google Ads bzw. im Meta Events Manager angelegt sind – sonst
            kommt die Conversion an, wird aber keiner Conversion-Aktion zugeordnet.
            <br />
            <strong>Google-Event</strong> ist der Name für gtag/dataLayer (und den CSV-Export).{' '}
            <strong>Ads-Label</strong> feuert zusätzlich die Ads-Conversion direkt
            (<code>send_to: AW-…/Label</code>) – nötig, wenn die Conversion nicht über GA4 oder GTM importiert wird.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {actions.length === 0 && (
            <p className="text-sm text-muted-foreground">Aktionskatalog konnte nicht geladen werden.</p>
          )}
          {actions.map((action) => {
            const entry = events[action.key];
            if (!entry) return null;
            return (
              <div key={action.key} className="rounded-md border p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium">{action.label}</div>
                    <div className="text-xs text-muted-foreground">{action.description}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {action.source === 'browser'
                        ? 'Wird im Browser gemeldet (gtag + Pixel).'
                        : 'Passiert im Office: Meta erhält sie serverseitig, Google über den CSV-Export unten.'}
                    </div>
                  </div>
                  <Switch
                    checked={entry.enabled}
                    onCheckedChange={(v) => setEventField(action.key, 'enabled', v)}
                    disabled={busy}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Google-Event</Label>
                    <Input
                      value={entry.googleEvent}
                      onChange={(e) => setEventField(action.key, 'googleEvent', e.target.value)}
                      placeholder={action.defaultGoogleEvent}
                      disabled={busy || !entry.enabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Google Ads Conversion-Label</Label>
                    <Input
                      value={entry.googleAdsLabel}
                      onChange={(e) => setEventField(action.key, 'googleAdsLabel', e.target.value)}
                      placeholder="optional, z. B. AbC-D_efGhIjKl"
                      disabled={busy || !entry.enabled || action.source === 'server'}
                    />
                    {action.source === 'server' && (
                      <p className="text-xs text-muted-foreground">
                        Nicht anwendbar: Offline-Conversions tragen den Namen, kein Label.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Meta-Event</Label>
                    <Input
                      value={entry.metaEvent}
                      onChange={(e) => setEventField(action.key, 'metaEvent', e.target.value)}
                      placeholder={action.defaultMetaEvent}
                      disabled={busy || !entry.enabled}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Google Ads Offline-Conversions
          </CardTitle>
          <CardDescription>
            Was im Office passiert, kann Google nicht selbst mitbekommen – es läuft kein Browser mit. Meta bekommt
            diese Conversions serverseitig; für Google wird eine CSV erzeugt, die in Google Ads unter{' '}
            <em>Zielvorhaben → Conversions importieren → Von Klicks</em> hochgeladen wird.
            <br />
            Enthalten sind nur Conversions mit Klick-ID (gclid/wbraid/gbraid) – ohne sie kann Google sie keinem
            Klick zuordnen. Heruntergeladene Zeilen gelten als exportiert und tauchen im nächsten Export nicht mehr
            auf.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={downloadExport} disabled={busy || exporting || pendingExport === 0}>
              {exporting ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              CSV herunterladen ({pendingExport})
            </Button>
            <Button variant="outline" onClick={loadConversions} disabled={busy}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Aktualisieren
            </Button>
            {pendingExport === 0 && (
              <span className="text-sm text-muted-foreground">Nichts zu exportieren.</span>
            )}
          </div>

          {conversions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine offenen Conversions aus dem Office.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Aktion</th>
                    <th className="py-2 pr-4 font-medium">Zeitpunkt</th>
                    <th className="py-2 pr-4 font-medium">Google</th>
                    <th className="py-2 pr-4 font-medium">Meta</th>
                  </tr>
                </thead>
                <tbody>
                  {conversions.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-2 pr-4">{row.actionLabel}</td>
                      <td className="py-2 pr-4">{formatDateTime(row.occurredAt)}</td>
                      <td className="py-2 pr-4">
                        {!row.hasGoogleClickId ? (
                          <span className="text-muted-foreground">keine Klick-ID</span>
                        ) : row.googleExportedAt ? (
                          `exportiert ${formatDateTime(row.googleExportedAt)}`
                        ) : (
                          'wartet auf Export'
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {row.metaSentAt ? (
                          `${row.metaEvent} · gesendet`
                        ) : row.metaError ? (
                          <span className="text-destructive">{row.metaError}</span>
                        ) : (
                          <span className="text-muted-foreground">nicht gesendet</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Meta (Facebook/Instagram)
          </CardTitle>
          <CardDescription>
            Der Pixel läuft im Browser, die Conversions API zusätzlich vom Server. Beide senden dieselbe Event-ID,
            damit Meta die Conversion nur einmal zählt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Meta Pixel-ID</Label>
              <Input
                value={settings.metaPixelId || ''}
                onChange={(e) => setSettings((prev) => ({ ...prev, metaPixelId: e.target.value }))}
                placeholder="123456789012345"
                disabled={busy}
              />
              <p className="text-xs text-muted-foreground">Rein numerisch, aus dem Meta Events Manager.</p>
            </div>
            <div className="space-y-2">
              <Label>Test-Event-Code (optional)</Label>
              <Input
                value={settings.metaCapiTestEventCode || ''}
                onChange={(e) => setSettings((prev) => ({ ...prev, metaCapiTestEventCode: e.target.value }))}
                placeholder="TEST12345"
                disabled={busy}
              />
              <p className="text-xs text-muted-foreground">
                Gesetzt landen alle Server-Events unter „Testereignisse“ statt in der echten Statistik. Für den
                Live-Betrieb wieder leeren.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Conversions API Token</Label>
            <Input
              type="password"
              value={capiToken}
              onChange={(e) => setCapiToken(e.target.value)}
              placeholder={tokenSet ? '•••••••• (gespeichert – leer lassen, um es zu behalten)' : 'Token einfügen'}
              disabled={busy}
              autoComplete="new-password"
            />
            <p className="text-xs text-muted-foreground">
              Wird nur serverseitig verwendet und nie wieder ausgeliefert.{' '}
              {tokenSet ? 'Ein Token ist hinterlegt.' : 'Aktuell ist kein Token hinterlegt.'}
            </p>
            {tokenSet && (
              <Button variant="outline" size="sm" onClick={clearToken} disabled={busy}>
                <Trash2 className="mr-2 h-4 w-4" />
                Token entfernen
              </Button>
            )}
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Server-Events senden (CAPI)</div>
              <div className="text-xs text-muted-foreground">
                Conversions zusätzlich vom Server melden – erreicht auch Besucher mit Adblocker. Benötigt Pixel-ID
                und Token.
              </div>
            </div>
            <Switch
              checked={!!settings.serverEventsEnabled}
              onCheckedChange={(v) => setSettings((prev) => ({ ...prev, serverEventsEnabled: v }))}
              disabled={busy}
            />
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={testAction}
                onChange={(e) => setTestAction(e.target.value)}
                disabled={busy || testing || actions.length === 0}
                aria-label="Aktion für das Test-Event"
              >
                {actions.map((action) => (
                  <option key={action.key} value={action.key}>
                    {action.label}
                  </option>
                ))}
              </select>
              <Button variant="outline" onClick={sendTestEvent} disabled={busy || testing || !tokenSet}>
                {testing ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Test-Event an Meta senden
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Prüft die gespeicherte Pixel-ID und das Token. Gesendet wird der Meta-Name, der für die gewählte
              Aktion <strong>gespeichert</strong> ist – Änderungen oben also vorher speichern.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={load} disabled={busy}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Neu laden
        </Button>
        <Button onClick={save} disabled={busy}>
          {saving ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Speichern...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Speichern
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
